/**
 * Activity reviews (TRAV23 activities-front merge).
 *
 * Distinct from `trust-you-review`, which is the hotel review module. Exports are
 * activity-prefixed because `getReviews` / `reviewsActions` are generic enough to
 * collide as this barrel grows.
 */

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';

import {
  IReview,
  IReviewRequest,
  IReviewRequestPhoto,
  IReviewResponse,
  ITotalReviewsSummary,
} from '@common-types';
import { Urls } from '@share/constants';
import { axiosInstance, getHeaders } from '@share/utils';

/** Also the "is there another page?" threshold, so a short page ends the infinite scroll. */
const REVIEWS_PAGE_SIZE = 10;
const FIRST_PAGE = 1;

export type ActivityReviewSort = 'MOST_RECENT' | 'MOST_HELPFUL' | 'HIGHEST_RATING';

export interface IActivityReviewsState {
  reviews: IReview[];
  summary: ITotalReviewsSummary | null;
  loading: boolean;
  error: string;
  sortBy: ActivityReviewSort;
  page: number;
  isFiltering: boolean;
  travelerPhotos: IReview[];
  searchText: string;
  hasMore: boolean;
}

const initialState: IActivityReviewsState = {
  reviews: [],
  summary: null,
  loading: false,
  error: '',
  sortBy: 'MOST_RECENT',
  page: FIRST_PAGE,
  isFiltering: false,
  travelerPhotos: [],
  searchText: '',
  hasMore: true,
};

const requestReviews = async (request: IReviewRequest | IReviewRequestPhoto) => {
  return axiosInstance.post(Urls.ActivityReviews, request, {
    ...getHeaders(),
  });
};

export const getActivityReviews = createAsyncThunk<
  { data: IReviewResponse; reset: boolean },
  { productCode: string; reset?: boolean }
>('activityReviews/getActivityReviews', async ({ productCode, reset = false }, { getState, rejectWithValue }) => {
  const state = (getState() as any).activityReviewsStore as IActivityReviewsState;

  const request: IReviewRequest = {
    ProductCode: productCode,
    PageSize: REVIEWS_PAGE_SIZE,
    // A sort or search change resets to page one without waiting for state to settle.
    Page: reset ? FIRST_PAGE : state.page,
    SortBy: state.sortBy,
    searchText: state.searchText,
  };

  try {
    const res = await requestReviews(request);

    if (!res.data) {
      return rejectWithValue('No data received');
    }

    return { data: res.data, reset };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch reviews');
  }
});

export const getActivityTravelerPhotos = createAsyncThunk<IReviewResponse, string>(
  'activityReviews/getActivityTravelerPhotos',
  async (productCode, { rejectWithValue }) => {
    const request: IReviewRequestPhoto = {
      ProductCode: productCode,
      SortBy: 'MOST_RECENT',
      photoOnly: true,
    };

    try {
      const res = await requestReviews(request);

      if (!res.data) {
        return rejectWithValue('No data received');
      }

      return res.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch photos');
    }
  },
);

const activityReviewsSlice = createSlice({
  name: 'activityReviews',
  initialState,
  reducers: {
    setSortBy: (state, { payload }: PayloadAction<ActivityReviewSort>) => {
      state.sortBy = payload;
      state.page = FIRST_PAGE;
    },
    incrementPage: (state) => {
      state.page += 1;
    },
    resetReviews: (state) => {
      state.reviews = [];
      state.summary = null;
      state.page = FIRST_PAGE;
      state.travelerPhotos = [];
      state.searchText = '';
      state.hasMore = true;
    },
    setSearchText: (state, { payload }: PayloadAction<string>) => {
      state.searchText = payload;
      state.page = FIRST_PAGE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActivityReviews.pending, (state, action) => {
        state.loading = true;
        state.error = '';

        // `isFiltering` drives a list-only spinner so the filter bar stays interactive.
        if (action.meta.arg.reset) {
          state.isFiltering = true;
        }
      })
      .addCase(getActivityReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.isFiltering = false;

        if (action.payload.reset) {
          state.reviews = action.payload.data.reviews;
        } else {
          state.reviews = [...state.reviews, ...action.payload.data.reviews];
        }

        state.summary = action.payload.data.totalReviewsSummary;
        // A short page means the supplier has nothing further.
        state.hasMore = action.payload.data.reviews.length >= REVIEWS_PAGE_SIZE;
      })
      .addCase(getActivityReviews.rejected, (state, action) => {
        state.loading = false;
        state.isFiltering = false;
        state.error = action.payload as string;
      })

      .addCase(getActivityTravelerPhotos.fulfilled, (state, action) => {
        state.travelerPhotos = action.payload.reviews;
      });
  },
});

export const activityReviewsActions = {
  ...activityReviewsSlice.actions,
  getActivityReviews,
  getActivityTravelerPhotos,
};

export const activityReviewsReducer = activityReviewsSlice.reducer;
